import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, Trash2, User, Link2, MessageSquare, GripVertical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { TASK_STATUSES, TASK_PRIORITIES, STATUS_COLOR, PRIORITY_COLOR, moneyExact, initials } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  title: "", description: "", status: "To Do", priority: "Medium",
  estimated_cost: "", actual_cost: "", start_date: "", due_date: "", assigned_to: "",
};

function useMembers(projectId) {
  return useQuery({
    queryKey: ["members", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/members`)).data.members,
  });
}

function TaskDialog({ projectId, currency, task, open, onOpenChange }) {
  const qc = useQueryClient();
  const editing = !!task;
  const { data: members } = useMembers(projectId);
  const [form, setForm] = useState(EMPTY);

  // initialize form when opening
  const initRef = useState(() => ({}))[0];
  if (open && initRef.id !== (task?.id || "new")) {
    initRef.id = task?.id || "new";
    setForm(task ? {
      title: task.title || "", description: task.description || "", status: task.status,
      priority: task.priority, estimated_cost: String(task.estimated_cost ?? ""),
      actual_cost: String(task.actual_cost ?? ""), start_date: task.start_date || "",
      due_date: task.due_date || "", assigned_to: task.assigned_to || "",
    } : { ...EMPTY });
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const { data: taskMsgs } = useQuery({
    queryKey: ["task-messages", task?.id],
    queryFn: async () => (await api.get(`/tasks/${task.id}/messages`)).data.messages,
    enabled: editing && open,
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        estimated_cost: parseFloat(form.estimated_cost || "0"),
        actual_cost: parseFloat(form.actual_cost || "0"),
        assigned_to: form.assigned_to || null,
        start_date: form.start_date || null,
        due_date: form.due_date || null,
      };
      if (editing) return (await api.put(`/tasks/${task.id}`, payload)).data.task;
      return (await api.post(`/projects/${projectId}/tasks`, payload)).data.task;
    },
    onSuccess: () => {
      toast.success(editing ? "Task updated" : "Task created");
      qc.invalidateQueries({ queryKey: ["tasks", projectId] });
      qc.invalidateQueries({ queryKey: ["dashboard", projectId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/tasks/${task.id}`)).data,
    onSuccess: () => {
      toast.success("Task removed");
      qc.invalidateQueries({ queryKey: ["tasks", projectId] });
      qc.invalidateQueries({ queryKey: ["dashboard", projectId] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">{editing ? "Task Detail" : "New Task"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
          <div className="space-y-2">
            <Label className="label-caps">Title</Label>
            <Input required value={form.title} onChange={set("title")} data-testid="task-title-input" className="h-12 bg-background" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Description</Label>
            <Textarea value={form.description} onChange={set("description")} data-testid="task-description-input" className="bg-background min-h-20" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-caps">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="h-12 bg-background" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger className="h-12 bg-background" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-caps">Est. Cost</Label>
              <Input type="number" min="0" step="0.01" value={form.estimated_cost} onChange={set("estimated_cost")}
                data-testid="task-estcost-input" className="h-12 bg-background font-data" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Actual Cost</Label>
              <Input type="number" min="0" step="0.01" value={form.actual_cost} onChange={set("actual_cost")}
                data-testid="task-actualcost-input" className="h-12 bg-background font-data" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-caps">Start Date</Label>
              <Input type="date" value={form.start_date} onChange={set("start_date")} className="h-12 bg-background" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Due Date</Label>
              <Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="task-due-input" className="h-12 bg-background" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Assignee</Label>
            <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "unassigned" ? "" : v }))}>
              <SelectTrigger className="h-12 bg-background" data-testid="task-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {(members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name} · {m.role_tier}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {editing && taskMsgs && taskMsgs.length > 0 && (
            <div className="space-y-2 border-t border-border pt-4">
              <Label className="label-caps flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Linked Messages</Label>
              <div className="space-y-2">
                {taskMsgs.map((m) => (
                  <div key={m.id} className="border border-border rounded px-3 py-2 text-sm bg-background">
                    <span className="font-semibold text-primary">{m.sender?.first_name}: </span>{m.body}
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {editing && (
              <Button type="button" variant="ghost" onClick={() => del.mutate()} data-testid="task-delete-button"
                className="h-12 text-destructive hover:text-destructive hover:bg-destructive/10 mr-auto">
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
            <Button type="submit" disabled={save.isPending} data-testid="task-save-button"
              className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
              {save.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : editing ? "Save Changes" : "Create Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaskCard({ task, currency, onClick, onDragStart }) {
  return (
    <div draggable onDragStart={onDragStart} onClick={onClick}
      data-testid={`task-card-${task.id}`}
      className="bg-card border border-border rounded-md p-3 cursor-pointer hover:border-primary transition-colors group"
      style={{ borderLeft: `3px solid ${STATUS_COLOR[task.status]}` }}>
      <div className="flex items-start gap-2">
        <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm leading-tight mb-2">{task.title}</div>
          <div className="flex items-center flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded"
              style={{ color: PRIORITY_COLOR[task.priority], backgroundColor: `${PRIORITY_COLOR[task.priority]}22` }}>
              {task.priority}
            </span>
            {task.due_date && <span className="text-xs text-muted-foreground font-data">{task.due_date}</span>}
          </div>
          <div className="flex items-center justify-between mt-2.5">
            <span className="text-xs font-data text-muted-foreground">{moneyExact(task.actual_cost, currency)}</span>
            {task.assignee ? (
              <span className="h-6 w-6 rounded bg-secondary grid place-items-center text-[10px] font-data font-bold" title={`${task.assignee.first_name} ${task.assignee.last_name}`}>
                {initials(task.assignee.first_name, task.assignee.last_name)}
              </span>
            ) : (
              <span className="h-6 w-6 rounded bg-background grid place-items-center"><User className="h-3 w-3 text-muted-foreground" /></span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TasksTab({ projectId, currency }) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const { data: tasks, isLoading } = useQuery({
    queryKey: ["tasks", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/tasks`)).data.tasks,
    refetchInterval: 12000,
  });

  const move = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/tasks/${id}`, { status })).data.task,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks", projectId] });
      qc.invalidateQueries({ queryKey: ["dashboard", projectId] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openNew = () => { setActiveTask(null); setDialogOpen(true); };
  const openTask = (t) => { setActiveTask(t); setDialogOpen(true); };

  const drop = (status) => {
    if (dragId) {
      const t = (tasks || []).find((x) => x.id === dragId);
      if (t && t.status !== status) move.mutate({ id: dragId, status });
    }
    setDragId(null); setDragOver(null);
  };

  if (isLoading) return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const byStatus = Object.fromEntries(TASK_STATUSES.map((s) => [s, (tasks || []).filter((t) => t.status === s)]));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">{tasks?.length || 0} tasks · drag cards to change status</div>
        <Button onClick={openNew} data-testid="new-task-button" className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
          <Plus className="h-5 w-5 mr-1" /> New Task
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {TASK_STATUSES.map((status) => (
          <div key={status}
            onDragOver={(e) => { e.preventDefault(); setDragOver(status); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={() => drop(status)}
            data-testid={`kanban-column-${status.replace(/\s/g, "-")}`}
            className={`rounded-md border transition-colors ${dragOver === status ? "border-primary bg-primary/5" : "border-border bg-[hsl(var(--sidebar))]"}`}>
            <div className="flex items-center justify-between px-3 h-11 border-b border-border">
              <span className="flex items-center gap-2 font-bold text-sm">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[status] }} />{status}
              </span>
              <span className="font-data text-sm text-muted-foreground">{byStatus[status].length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-32">
              {byStatus[status].map((t) => (
                <TaskCard key={t.id} task={t} currency={currency}
                  onClick={() => openTask(t)}
                  onDragStart={() => setDragId(t.id)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <TaskDialog projectId={projectId} currency={currency} task={activeTask} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
